import React, { useContext, useEffect, useState } from "react";
import { TranslatorContext } from "../../../context/Translator";
import { Row, Col, Card, Form } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { ButtonComponent } from "../../../components/elements";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { toast, Toaster } from "react-hot-toast";

export default function LabTestTemplateEditPage() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();
    const { id } = useParams();
    const AuthToken = localStorage.getItem("authToken");

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [labTests, setLabTests] = useState([]);
    const [petBreeds, setPetBreeds] = useState([]);

    const [selectedLabTest, setSelectedLabTest] = useState(null);
    const [selectedPetBreed, setSelectedPetBreed] = useState(null);
    const [templateName, setTemplateName] = useState("");

    const [items, setItems] = useState([]);

    useEffect(() => {
        fetchLabTests();
        fetchPetBreeds();
        fetchTemplate();
    }, [id]);

    const fetchLabTests = async () => {
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/lab-tests`,
                {
                    headers: { Authorization: `Bearer ${AuthToken}` },
                }
            );
            setLabTests(res.data || []);
        } catch (err) {
            toast.error("Failed to load lab tests");
        }
    };

    const fetchPetBreeds = async () => {
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/lab-template/pet-breeds`,
                {
                    headers: { Authorization: `Bearer ${AuthToken}` },
                }
            );
            setPetBreeds(res.data.data || []);
        } catch (err) {
            toast.error("Failed to load pet breeds");
        }
    };

    const fetchTemplate = async () => {
        try {
            setLoading(true);
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/lab-test-templates/${id}`,
                {
                    headers: { Authorization: `Bearer ${AuthToken}` },
                }
            );
            
            const template = res.data;
            
            // Set basic info
            setTemplateName(template.name || "");
            
            // Find and set selected lab test
            const labTestOption = {
                value: template.lab_test_id,
                label: template.lab_test?.name || "Loading..."
            };
            setSelectedLabTest(labTestOption);
            
            // Find and set selected pet breed
            const petBreedOption = {
                value: template.pet_breed_id,
                label: template.pet_breed?.name || "Loading..."
            };
            setSelectedPetBreed(petBreedOption);
            
            // Convert sections and fields to items array
            const convertedItems = [];
            
            if (template.sections && template.sections.length > 0) {
                template.sections.forEach((section, sectionIndex) => {
                    // Add header item
                    if (section.title) {
                        convertedItems.push({
                            type: "header",
                            title: section.title,
                            order: section.order || sectionIndex,
                            id: section.id // Keep original ID for reference
                        });
                    }
                    
                    // Add field items for this section
                    if (section.fields && section.fields.length > 0) {
                        section.fields.forEach((field, fieldIndex) => {
                            convertedItems.push({
                                type: "field",
                                field_name: field.field_name,
                                unit: field.unit || "",
                                normal_min: field.normal_min || "",
                                normal_max: field.normal_max || "",
                                order: field.order || fieldIndex,
                                id: field.id // Keep original ID for reference
                            });
                        });
                    }
                });
            }
            
            setItems(convertedItems);
            
        } catch (err) {
            toast.error("Failed to load template");
            navigate("/lab-test-templates");
        } finally {
            setLoading(false);
        }
    };

    // =========================
    // SAFE STATE UPDATE HELPERS
    // =========================
    const addHeader = () => {
        setItems(prev => [
            ...prev,
            {
                type: "header",
                title: "",
                order: prev.length
            }
        ]);
        toast.success("Header added successfully");
    };

    const addField = () => {
        setItems(prev => [
            ...prev,
            {
                type: "field",
                field_name: "",
                unit: "",
                normal_min: "",
                normal_max: "",
                order: prev.length
            }
        ]);
        toast.success("Field added successfully");
    };

    const updateItem = (index, key, value) => {
        setItems(prev => {
            const updated = [...prev];
            if (!updated[index]) return prev;
            updated[index] = {
                ...updated[index],
                [key]: value
            };
            return updated;
        });
    };

    const removeItem = (index) => {
        // Add confirmation dialog
        if (window.confirm("Are you sure you want to remove this item?")) {
            setItems(prev => prev.filter((_, i) => i !== index));
            toast.success("Item removed successfully");
        }
    };

    const moveItemUp = (index) => {
        if (index === 0) return;
        setItems(prev => {
            const updated = [...prev];
            [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
            return updated.map((item, idx) => ({ ...item, order: idx }));
        });
        toast.success("Item moved up");
    };

    const moveItemDown = (index) => {
        if (index === items.length - 1) return;
        setItems(prev => {
            const updated = [...prev];
            [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
            return updated.map((item, idx) => ({ ...item, order: idx }));
        });
        toast.success("Item moved down");
    };

    // Validate numeric ranges
    const validateNumericRanges = (items) => {
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.type === "field") {
                const min = item.normal_min ? parseFloat(item.normal_min) : null;
                const max = item.normal_max ? parseFloat(item.normal_max) : null;
                
                if (min !== null && max !== null && min > max) {
                    return {
                        isValid: false,
                        error: `Min value cannot be greater than Max value in field "${item.field_name || `row ${i + 1}`}"`
                    };
                }
            }
        }
        return { isValid: true, error: null };
    };

    // =========================
    // SUBMIT
    // =========================
    const handleSubmit = async () => {
        // Validate lab test selection
        if (!selectedLabTest) {
            toast.error("Please select lab test");
            return;
        }

        // Validate pet breed selection
        if (!selectedPetBreed) {
            toast.error("Please select pet breed");
            return;
        }

        // Validate items exist
        if (items.length === 0) {
            toast.error("Please add header or fields");
            return;
        }

        // Validate individual items
        for (let i = 0; i < items.length; i++) {
            const item = items[i];

            if (item.type === "header" && !item.title?.trim()) {
                toast.error(`Header title required at row ${i + 1}`);
                return;
            }

            if (item.type === "field" && !item.field_name?.trim()) {
                toast.error(`Field name required at row ${i + 1}`);
                return;
            }
        }

        // Validate numeric ranges
        const rangeValidation = validateNumericRanges(items);
        if (!rangeValidation.isValid) {
            toast.error(rangeValidation.error);
            return;
        }

        // Prepare payload
        const payload = {
            lab_test_id: selectedLabTest.value,
            pet_breed_id: selectedPetBreed.value,
            name: templateName || null,
            items: items.map((item, index) => ({
                type: item.type,
                title: item.title,
                field_name: item.field_name,
                unit: item.unit,
                normal_min: item.normal_min ? parseFloat(item.normal_min) : null,
                normal_max: item.normal_max ? parseFloat(item.normal_max) : null,
                order: index
            }))
        };

        try {
            setSubmitting(true);

            const response = await axios.put(
                `${process.env.REACT_APP_API_URL}/lab-test-templates/${id}`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${AuthToken}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            if (response.data) {
                toast.success("Template updated successfully");
                
                setTimeout(() => {
                    navigate("/lab-test-templates");
                }, 1500);
            }

        } catch (err) {
            if (err.response?.data?.errors) {
                const errors = err.response.data.errors;
                Object.values(errors).forEach(errorMsg => {
                    if (Array.isArray(errorMsg)) {
                        errorMsg.forEach(msg => toast.error(msg));
                    } else {
                        toast.error(errorMsg);
                    }
                });
            } else if (err.response?.data?.message) {
                toast.error(err.response.data.message);
            } else {
                toast.error("Error updating template. Please try again.");
            }
            console.error("Update error:", err);
        } finally {
            setSubmitting(false);
        }
    };

    const labTestOptions = (labTests || []).map(t => ({
        value: t.id,
        label: t.name
    }));

    const petBreedOptions = (petBreeds || []).map(breed => ({
        value: breed.id,
        label: breed.name
    }));

    if (loading) {
        return (
            <PageLayout>
                <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <h4 className="mt-3">Loading template...</h4>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Toaster position="top-right" toastOptions={{
                duration: 3000,
                style: {
                    background: '#363636',
                    color: '#fff',
                },
                success: {
                    duration: 3000,
                    iconTheme: {
                        primary: '#4caf50',
                        secondary: '#fff',
                    },
                },
                error: {
                    duration: 4000,
                    iconTheme: {
                        primary: '#f44336',
                        secondary: '#fff',
                    },
                },
            }} />

            <Card className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h3>Edit Lab Test Template</h3>
                    <ButtonComponent
                        className="btn btn-secondary"
                        onClick={() => navigate("/lab-test-templates")}
                        text="Back to Templates"
                    />
                </div>

                {/* TOP - Lab Test, Pet Breed, and Template Name */}
                <Row className="mb-4">
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label className="fw-bold">Lab Test <span className="text-danger">*</span></Form.Label>
                            <Select
                                options={labTestOptions}
                                value={selectedLabTest}
                                onChange={setSelectedLabTest}
                                placeholder="Select a lab test..."
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        border: "none",
                                        borderBottom: "1px solid #ced4da",
                                        borderRadius: 0,
                                        boxShadow: "none",
                                        "&:hover": {
                                            borderBottom: "1px solid #ced4da"
                                        }
                                    })
                                }}
                            />
                        </Form.Group>
                    </Col>

                    <Col md={4}>
                        <Form.Group>
                            <Form.Label className="fw-bold">Pet Breed <span className="text-danger">*</span></Form.Label>
                            <Select
                                options={petBreedOptions}
                                value={selectedPetBreed}
                                onChange={setSelectedPetBreed}
                                placeholder="Select a pet breed..."
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        border: "none",
                                        borderBottom: "1px solid #ced4da",
                                        borderRadius: 0,
                                        boxShadow: "none",
                                        "&:hover": {
                                            borderBottom: "1px solid #ced4da"
                                        }
                                    })
                                }}
                            />
                        </Form.Group>
                    </Col>

                    <Col md={4}>
                        <Form.Group>
                            <Form.Label className="fw-bold">Template Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={templateName}
                                onChange={(e) => setTemplateName(e.target.value)}
                                placeholder="Enter template name (optional)"
                                style={{
                                    border: "none",
                                    borderBottom: "1px solid #ced4da",
                                    borderRadius: 0,
                                    boxShadow: "none",
                                    paddingLeft: 0,
                                    background: "transparent"
                                }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                {/* BUTTONS */}
                <div className="d-flex gap-2 mb-4">
                    <ButtonComponent
                        className="btn btn-primary"
                        onClick={addHeader}
                        text="+ Add Header"
                    />
                    <ButtonComponent
                        className="btn btn-secondary"
                        onClick={addField}
                        text="+ Add Field"
                    />
                </div>

                {/* EMPTY STATE */}
                {items.length === 0 && (
                    <div className="p-5 text-center border rounded bg-light">
                        <i className="material-icons" style={{ fontSize: '48px', color: '#6c757d' }}>description</i>
                        <h5 className="mt-3">No items added yet</h5>
                        <p className="text-muted">Click "Add Header" or "Add Field" to get started.</p>
                    </div>
                )}

                {/* LIST OF ITEMS */}
                {items.map((item, index) => (
                    <div key={index} className="p-3 mb-3" style={{ border: "1px solid #e0e0e0", borderRadius: "8px", backgroundColor: "#fafafa" }}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <span className={`badge ${item.type === "header" ? "bg-primary" : "bg-info"}`}>
                                    {item.type === "header" ? "HEADER" : "FIELD"} #{index + 1}
                                </span>
                            </div>
                            <div className="d-flex gap-2">
                                <ButtonComponent
                                    className="btn btn-sm btn-secondary"
                                    onClick={() => moveItemUp(index)}
                                    text="↑"
                                    disabled={index === 0}
                                    title="Move Up"
                                />
                                <ButtonComponent
                                    className="btn btn-sm btn-secondary"
                                    onClick={() => moveItemDown(index)}
                                    text="↓"
                                    disabled={index === items.length - 1}
                                    title="Move Down"
                                />
                                <ButtonComponent
                                    className="btn btn-sm btn-danger"
                                    onClick={() => removeItem(index)}
                                    text="Remove"
                                    title="Remove Item"
                                />
                            </div>
                        </div>

                        {/* HEADER */}
                        {item.type === "header" && (
                            <Row>
                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold">Header Title <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={item.title || ""}
                                            onChange={(e) =>
                                                updateItem(index, "title", e.target.value)
                                            }
                                            placeholder="Enter header title"
                                            style={{
                                                border: "none",
                                                borderBottom: "1px solid #ced4da",
                                                borderRadius: 0,
                                                boxShadow: "none",
                                                paddingLeft: 0,
                                                background: "transparent"
                                            }}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}

                        {/* FIELD */}
                        {item.type === "field" && (
                            <Row>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold">Field Name <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={item.field_name || ""}
                                            onChange={(e) =>
                                                updateItem(index, "field_name", e.target.value)
                                            }
                                            placeholder="Enter field name"
                                            style={{
                                                border: "none",
                                                borderBottom: "1px solid #ced4da",
                                                borderRadius: 0,
                                                boxShadow: "none",
                                                paddingLeft: 0,
                                                background: "transparent"
                                            }}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold">Unit</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={item.unit || ""}
                                            onChange={(e) =>
                                                updateItem(index, "unit", e.target.value)
                                            }
                                            placeholder="e.g., mg/dL"
                                            style={{
                                                border: "none",
                                                borderBottom: "1px solid #ced4da",
                                                borderRadius: 0,
                                                boxShadow: "none",
                                                paddingLeft: 0,
                                                background: "transparent"
                                            }}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={2}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold">Min Range</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="any"
                                            value={item.normal_min || ""}
                                            onChange={(e) =>
                                                updateItem(index, "normal_min", e.target.value)
                                            }
                                            placeholder="Min value"
                                            style={{
                                                border: "none",
                                                borderBottom: "1px solid #ced4da",
                                                borderRadius: 0,
                                                boxShadow: "none",
                                                paddingLeft: 0,
                                                background: "transparent"
                                            }}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold">Max Range</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="any"
                                            value={item.normal_max || ""}
                                            onChange={(e) =>
                                                updateItem(index, "normal_max", e.target.value)
                                            }
                                            placeholder="Max value"
                                            style={{
                                                border: "none",
                                                borderBottom: "1px solid #ced4da",
                                                borderRadius: 0,
                                                boxShadow: "none",
                                                paddingLeft: 0,
                                                background: "transparent"
                                            }}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}
                    </div>
                ))}

                {/* SUBMIT BUTTON */}
                {items.length > 0 && (
                    <div className="mt-4">
                        <ButtonComponent
                            className="btn btn-success w-100"
                            onClick={handleSubmit}
                            text={submitting ? "Updating Template..." : "Update Template"}
                            disabled={submitting}
                        />
                    </div>
                )}
            </Card>
        </PageLayout>
    );
}